/**
 * InlinePicker — generic база для inline-выбора значения в стиле Linear/Notion.
 *
 * Показывает компактный `display` (текст/иконка/аватар) с hover-фоном; по клику
 * открывается Mantine Popover, внутри которого consumer рендерит свой control
 * (UserSelect, SegmentSelect и т.д.) через `children`. Состоянием open управляет
 * родитель — это позволяет конкретным обёрткам закрывать popover после выбора
 * (`onChange → setOpened(false)`).
 *
 * Если `canEdit=false` — рендерится только `display`, без popover и hover-эффекта.
 *
 * Примеры обёрток: UserPicker (identity), SegmentPicker (crm), SalesAreaPicker и т.д.
 */
import { useState, type ReactNode } from 'react';
import { Box, Popover } from '@mantine/core';

export interface InlinePickerProps {
  /** Что показывать в свёрнутом виде (имя + аватар, имя + иконка и т.д.) */
  display: ReactNode;
  /** Содержимое popover-а: control для выбора значения (UserSelect, etc.) */
  children: ReactNode;
  /** Контролируемое состояние открытия popover-а. */
  opened: boolean;
  onOpenChange: (opened: boolean) => void;
  /** Если false — popover не открывается, display рендерится как обычный текст. */
  canEdit?: boolean;
  /** Ширина popover-а. По умолчанию 260. */
  width?: number | string;
}

export function InlinePicker({
  display,
  children,
  opened,
  onOpenChange,
  canEdit = true,
  width = 260,
}: InlinePickerProps) {
  const [hovered, setHovered] = useState(false);

  if (!canEdit) {
    return <Box>{display}</Box>;
  }

  return (
    <Popover
      opened={opened}
      onChange={onOpenChange}
      position='bottom-start'
      width={width}
      withinPortal
      shadow='md'
    >
      <Popover.Target>
        <Box
          onClick={() => onOpenChange(!opened)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: 4,
            background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
            transition: 'background 0.1s ease',
            display: 'inline-flex',
            minWidth: 0,
          }}
        >
          {display}
        </Box>
      </Popover.Target>
      <Popover.Dropdown p='xs'>{children}</Popover.Dropdown>
    </Popover>
  );
}
